import React, { useState, useEffect, useCallback, useRef } from 'react';
import { User, Building, FileText, Globe, Wand2, Loader2, Plus as PlusIcon, ChevronRight, Brain, AlertCircle } from 'lucide-react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import configService, { Language } from '../../services/configService';
import { toast } from 'react-hot-toast';
import { API_BASE_URL } from '../../services/apiClient';

interface CustomInterviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: CustomInterviewFormData) => Promise<void>;
  initialData?: Partial<CustomInterviewFormData>;
}

export interface CustomInterviewFormData {
  jobRole: string;
  companyName: string;
  interviewRoundType: string;
  customRoundType?: string;
  jobDescription: string;
  interviewLanguage: string;
  interviewDifficulty: number;
}

// Helper function to parse URL parameters
const parseUrlParams = (): Partial<CustomInterviewFormData> => {
  const urlParams = new URLSearchParams(window.location.search);
  
  // Helper function to safely decode URI component
  const safeDecodeURIComponent = (str: string | null): string => {
    if (!str) return '';
    try {
      return decodeURIComponent(str);
    } catch (error) {
      console.warn('Failed to decode URI component:', str, error);
      // Return the original string if decoding fails
      return str;
    }
  };
  
  return {
    jobRole: urlParams.get('jobRole') || '',
    companyName: urlParams.get('company') || '',
    interviewRoundType: urlParams.get('roundType') || '',
    jobDescription: safeDecodeURIComponent(urlParams.get('jobDescription')),
    interviewLanguage: urlParams.get('language') || 'English'
  };
};

export const CustomInterviewModal: React.FC<CustomInterviewModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData
}) => {
  // Debug logging
  React.useEffect(() => {
    console.log('🔍 CustomInterviewModal props:', { isOpen, hasOnClose: !!onClose, hasOnSubmit: !!onSubmit });
  }, [isOpen, onClose, onSubmit]);
  
  // Form states
  const [jobRole, setJobRole] = useState(initialData?.jobRole || '');
  const [companyName, setCompanyName] = useState(initialData?.companyName || '');
  const [interviewRoundType, setInterviewRoundType] = useState(initialData?.interviewRoundType || '');
  const [customRoundType, setCustomRoundType] = useState(initialData?.customRoundType || '');
  const [jobDescription, setJobDescription] = useState(initialData?.jobDescription || '');
  const [interviewLanguage, setInterviewLanguage] = useState(initialData?.interviewLanguage || 'English');
  const [interviewDifficulty, setInterviewDifficulty] = useState(initialData?.interviewDifficulty || 3);
  
  // AI generation states
  const [aiDescription, setAiDescription] = useState('');
  const [isGeneratingFormData, setIsGeneratingFormData] = useState(false);
  
  // Other states
  const [languages, setLanguages] = useState<Language[]>([]);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Validation states
  const [validationErrors, setValidationErrors] = useState<{
    jobRole: string;
    companyName: string;
    interviewRoundType: string;
    customRoundType: string;
    jobDescription: string;
    interviewLanguage: string;
  }>({
    jobRole: '',
    companyName: '',
    interviewRoundType: '',
    customRoundType: '',
    jobDescription: '',
    interviewLanguage: ''
  });

  // Parse URL parameters and populate form when modal opens
  useEffect(() => {
    if (isOpen) {
      const urlData = parseUrlParams();
      console.log('🔗 URL parameters parsed:', urlData);
      
      // Only populate if we have meaningful data from URL
      if (urlData.jobRole || urlData.companyName || urlData.jobDescription) {
        console.log('📝 Populating form with URL data:', {
          jobRole: urlData.jobRole,
          companyName: urlData.companyName,
          interviewRoundType: urlData.interviewRoundType,
          jobDescription: urlData.jobDescription?.substring(0, 100) + '...',
          interviewLanguage: urlData.interviewLanguage
        });
        
        setJobRole(urlData.jobRole || jobRole);
        setCompanyName(urlData.companyName || companyName);
        setInterviewRoundType(urlData.interviewRoundType || interviewRoundType);
        setJobDescription(urlData.jobDescription || jobDescription);
        setInterviewLanguage(urlData.interviewLanguage || interviewLanguage);
        
        // Generate AI description from URL data
        if (urlData.jobRole && urlData.companyName) {
          const aiDesc = `${urlData.interviewRoundType || 'Technical'} round at ${urlData.companyName} for ${urlData.jobRole} position`;
          setAiDescription(aiDesc);
          console.log('🤖 Generated AI description:', aiDesc);
        }
      }
    }
  }, [isOpen]);

  // Handle AI generation
  const handleGenerateFormData = useCallback(async () => {
    if (aiDescription.trim().length < 15) {
      toast.error('Please provide at least 15-20 words to generate form data');
      return;
    }

    setIsGeneratingFormData(true);
    try {
      // For now, use a simple parsing approach since the AI endpoint might not exist
      // This provides a fallback that works immediately
      const words = aiDescription.toLowerCase().split(' ');
      
      // Extract round type
      let roundType = 'Technical';
      if (words.includes('hr')) roundType = 'HR';
      else if (words.includes('behavioral')) roundType = 'Behavioral';
      else if (words.includes('case') && words.includes('study')) roundType = 'Case Study';
      else if (words.includes('system') && words.includes('design')) roundType = 'System Design';
      else if (words.includes('technical')) roundType = 'Technical';
      
      // If we already have data from URL params or initialData, use that as base
      const urlData = parseUrlParams();
      if (urlData.jobRole || initialData?.jobRole) setJobRole(urlData.jobRole || initialData?.jobRole || '');
      if (urlData.companyName || initialData?.companyName) setCompanyName(urlData.companyName || initialData?.companyName || '');
      if (urlData.jobDescription || initialData?.jobDescription) setJobDescription(urlData.jobDescription || initialData?.jobDescription || '');
      
      // Set the extracted round type
      setInterviewRoundType(roundType);
      
      // Try the AI endpoint as well (optional - may not exist)
      try {
        const response = await fetch(`${API_BASE_URL}/interviews/generate-form-data`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('mocktrail-token')}`
          },
          body: JSON.stringify({ description: aiDescription })
        });

        if (response.ok) {
          const data = await response.json();
          
          // Update form fields with AI-generated data
          if (data.jobRole) setJobRole(data.jobRole);
          if (data.company) setCompanyName(data.company);
          if (data.roundType) {
            if (['Technical', 'HR', 'Behavioral', 'Case Study', 'System Design'].includes(data.roundType)) {
              setInterviewRoundType(data.roundType);
            } else {
              setInterviewRoundType('Other');
              setCustomRoundType(data.roundType);
            }
          }
          if (data.jobDescription) setJobDescription(data.jobDescription);
          if (data.language) setInterviewLanguage(data.language);
        } else if (response.status === 404) {
          // AI endpoint doesn't exist, which is fine - just use simple parsing
          console.debug('AI generation endpoint not available, using simple parsing');
        }
      } catch (apiError: any) {
        // Only log if it's not a network/404 error
        if (!apiError.message?.includes('Failed to fetch')) {
          console.debug('AI generation fallback to simple parsing');
        }
      }
      
      toast.success('Form fields generated successfully!');
    } catch (error) {
      console.error('Error generating form data:', error);
      toast.error('Failed to generate form data. Please try again.');
    } finally {
      setIsGeneratingFormData(false);
    }
  }, [aiDescription, initialData]);

  // Update form fields when initialData changes
  useEffect(() => {
    if (initialData) {
      setJobRole(initialData.jobRole || '');
      setCompanyName(initialData.companyName || '');
      setInterviewRoundType(''); // We don't get correct value from intent so always set '' and let user choose
      setCustomRoundType('');
      setJobDescription(initialData.jobDescription || '');
      setInterviewLanguage(initialData.interviewLanguage || 'English');
    }
  }, [initialData]);

  // Track if we've already attempted auto-generation for this modal session
  const hasAttemptedAutoGenerate = useRef(false);

  // Auto-generate AI description and trigger generation when modal opens with data
  useEffect(() => {
    // Check for data from either initialData props or URL parameters
    const urlData = parseUrlParams();
    const hasJobData = (initialData?.jobRole && initialData?.companyName) || (urlData.jobRole && urlData.companyName);
    
    if (isOpen && hasJobData) {
      // Use URL data if available, otherwise fall back to initialData
      const jobRole = urlData.jobRole || initialData?.jobRole || '';
      const companyName = urlData.companyName || initialData?.companyName || '';
      const roundType = urlData.interviewRoundType || initialData?.interviewRoundType || '';
      
      const aiDesc = `Interview at ${companyName} for ${jobRole} position`;
      setAiDescription(aiDesc);
      
      // Only attempt auto-generation once per modal session
      if (!hasAttemptedAutoGenerate.current) {
        hasAttemptedAutoGenerate.current = true;
        
        // Automatically trigger AI generation after a short delay
        const timer = setTimeout(() => {
          if (aiDesc.length >= 15 && !isGeneratingFormData) {
            handleGenerateFormData();
          }
        }, 800);
        
        return () => clearTimeout(timer);
      }
    }
    
    // Reset the flag when modal closes
    if (!isOpen) {
      hasAttemptedAutoGenerate.current = false;
    }
  }, [isOpen, initialData?.jobRole, initialData?.companyName, initialData?.jobDescription, initialData?.interviewRoundType]);

  // Load languages on mount
  useEffect(() => {
    const loadLanguages = async () => {
      setIsLoadingLanguages(true);
      try {
        const languagesData = await configService.getLanguages();
        setLanguages(languagesData);
      } catch (error) {
        console.error('Error loading languages:', error);
        toast.error('Failed to load languages');
      } finally {
        setIsLoadingLanguages(false);
      }
    };

    loadLanguages();
  }, []);

  // Validation functions
  const validateField = (fieldName: string, value: string): string => {
    console.log('🔍 Validating field:', fieldName, 'with value:', value);
    switch (fieldName) {
      case 'jobRole':
        return value.trim() === '' ? 'Please enter a job role' : '';
      case 'companyName':
        return value.trim() === '' ? 'Please enter a company name' : '';
      case 'interviewRoundType':
        return value.trim() === '' ? 'Please select a round type' : '';
      case 'customRoundType':
        return interviewRoundType === 'Other' && value.trim() === '' ? 'Please enter a custom round type' : '';
      case 'jobDescription':
        return value.trim() === '' ? 'Please enter a job description' : '';
      case 'interviewLanguage':
        return value.trim() === '' ? 'Please select an interview language' : '';
      default:
        return '';
    }
  };

  // Update validation errors when fields change
  useEffect(() => {
    setValidationErrors({
      jobRole: validateField('jobRole', jobRole),
      companyName: validateField('companyName', companyName),
      interviewRoundType: validateField('interviewRoundType', interviewRoundType),
      customRoundType: validateField('customRoundType', customRoundType),
      jobDescription: validateField('jobDescription', jobDescription),
      interviewLanguage: validateField('interviewLanguage', interviewLanguage)
    });
  }, [jobRole, companyName, interviewRoundType, customRoundType, jobDescription, interviewLanguage]);

  // Check if form is complete
  const isFormComplete = jobRole.trim() !== '' &&
    companyName.trim() !== '' &&
    interviewRoundType.trim() !== '' &&
    (interviewRoundType === 'Other' ? customRoundType.trim() !== '' : true) &&
    jobDescription.trim() !== '' &&
    interviewLanguage.trim() !== '' &&
    interviewDifficulty > 0;

  // Get the first validation error for tooltip
  const getFirstValidationError = (): string => {
    if (validationErrors.jobRole) return validationErrors.jobRole;
    if (validationErrors.companyName) return validationErrors.companyName;
    if (validationErrors.interviewRoundType) return validationErrors.interviewRoundType;
    if (validationErrors.customRoundType) return validationErrors.customRoundType;
    if (validationErrors.jobDescription) return validationErrors.jobDescription;
    if (validationErrors.interviewLanguage) return validationErrors.interviewLanguage;
    return '';
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormComplete) {
      const firstError = getFirstValidationError();
      toast.error(firstError || 'Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const formData: CustomInterviewFormData = {
        jobRole,
        companyName,
        interviewRoundType: interviewRoundType === 'Other' ? customRoundType : interviewRoundType,
        customRoundType: interviewRoundType === 'Other' ? customRoundType : undefined,
        jobDescription,
        interviewLanguage,
        interviewDifficulty
      };

      console.log('📤 Submitting form data from modal...');
      await onSubmit(formData);
      console.log('✅ onSubmit completed successfully');
      // Modal will be closed by parent component on success
    } catch (error) {
      console.error('❌ Error in modal form submission:', error);
      // Error handling is done in the parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset form fields after a delay to avoid visual glitches
      const timeout = setTimeout(() => {
        if (!initialData) {
          setJobRole('');
          setCompanyName('');
          setInterviewRoundType('');
          setCustomRoundType('');
          setJobDescription('');
          setInterviewLanguage('English');
          setInterviewDifficulty(3);
          setAiDescription('');
        }
      }, 300);
      
      return () => clearTimeout(timeout);
    }
  }, [isOpen, initialData]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Custom Interview"
      maxWidth="600px"
      preventOutsideClick={true}
    >
      <form onSubmit={handleSubmit} className="p-6">
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-6">
          Practice for your next interview with MockTrail. Customize the Company, Job Role, Round type and more based on your needs.
        </p>

        {/* Manual Form Section */}
        <div className="flex items-center gap-2 mb-4">
          <h4 className="text-lg font-heading font-semibold text-text-primary-light dark:text-text-primary-dark">
            Enter Interview Details
          </h4>
        </div>

        <div className="space-y-4 mb-6">
          {/* Job Role */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <User size={18} className="text-primary-light dark:text-primary-dark" />
              <label className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark h-5">
                Job Role
              </label>
            </div>
            <div className={`rounded-lg border py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all ${
              validationErrors.jobRole ? 'border-red-500 dark:border-red-400' : 'border-border-light dark:border-border-dark'
            }`}>
              <input
                type="text"
                placeholder="e.g. Senior Software Engineer, Product Manager, Data Scientist..."
                className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm"
                value={jobRole}
                onChange={(e) => setJobRole(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            {validationErrors.jobRole && (
              <p className="text-red-500 dark:text-red-400 text-xs mt-2 flex items-center gap-1">
                <AlertCircle size={12} />
                {validationErrors.jobRole}
              </p>
            )}
          </div>

          {/* Company Name */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Building size={18} className="text-primary-light dark:text-primary-dark" />
              <label className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark h-5">
                Company Name
              </label>
            </div>
            <div className={`rounded-lg border py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all ${
              validationErrors.companyName ? 'border-red-500 dark:border-red-400' : 'border-border-light dark:border-border-dark'
            }`}>
              <input
                type="text"
                placeholder="e.g. Google, Microsoft, Apple, or any company name..."
                className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            {validationErrors.companyName && (
              <p className="text-red-500 dark:text-red-400 text-xs mt-2 flex items-center gap-1">
                <AlertCircle size={12} />
                {validationErrors.companyName}
              </p>
            )}
          </div>

          {/* Interview Round Type */}
          <div>
                                 <div className="flex items-center gap-2 mb-2">
                   <FileText size={18} className="text-primary-light dark:text-primary-dark" />
                   <label className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark h-5">
                     Interview Round Type
                   </label>
                 </div>
                   <div className={`rounded-lg border py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all relative ${
                     validationErrors.interviewRoundType ? 'border-red-500 dark:border-red-400' : 'border-border-light dark:border-border-dark'
                   }`}>
                                         <select
                       className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm appearance-none pr-8"
                       value={interviewRoundType}
                       onChange={(e) => {
                         setInterviewRoundType(e.target.value);
                         if (e.target.value !== 'Other') {
                           setCustomRoundType(''); // Clear custom input when selecting predefined option
                         }
                       }}
                       disabled={isSubmitting}
                     >
                    <option value="">Select round type</option>
                    <option value="Technical Round">Technical Round</option>
                    <option value="Behavioral Round">Behavioral Round</option>
                    <option value="System Design Round">System Design Round</option>
                    <option value="Coding Round">Coding Round</option>
                    <option value="Case Study Round">Case Study Round</option>
                    <option value="Product Management Round">Product Management Round</option>
                    <option value="Leadership Round">Leadership Round</option>
                    <option value="Final Round">Final Round</option>
                    <option value="HR Round">HR Round</option>
                    <option value="General Interview">General Interview</option>
                    <option value="Other">Other (Custom)</option>
                  </select>
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <ChevronRight size={16} className="text-text-tertiary-light dark:text-text-tertiary-dark transform rotate-90" />
                  </div>
                </div>
                {validationErrors.interviewRoundType && (
                  <p className="text-red-500 dark:text-red-400 text-xs mt-2 flex items-center gap-1">
                    <AlertCircle size={12} />
                    {validationErrors.interviewRoundType}
                  </p>
                )}
                
                                 {/* Custom round type input - shown when "Other" is selected */}
                 {interviewRoundType === 'Other' && (
                   <div className="mt-3">
                     <div className={`rounded-lg border py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all ${
                       validationErrors.customRoundType ? 'border-red-500 dark:border-red-400' : 'border-border-light dark:border-border-dark'
                     }`}>
                       <input
                         type="text"
                         placeholder="e.g. AI/ML Round, DevOps Round, Sales Pitch, Design Critique..."
                         className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm"
                         value={customRoundType}
                         onChange={(e) => setCustomRoundType(e.target.value)}
                         disabled={isSubmitting}
                       />
                     </div>
                     {validationErrors.customRoundType && (
                       <p className="text-red-500 dark:text-red-400 text-xs mt-2 flex items-center gap-1">
                         <AlertCircle size={12} />
                         {validationErrors.customRoundType}
                       </p>
                     )}
                   </div>
                 )}
              </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <Brain size={18} className="text-primary-light dark:text-primary-dark" />
              <label className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark h-5">
                Interview Difficulty
              </label>
            </div>
            <div className="rounded-lg border border-border-light dark:border-border-dark py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all relative">
              <select
                value={interviewDifficulty}
                onChange={(e) => setInterviewDifficulty(Number(e.target.value))}
                className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm appearance-none pr-8"
                disabled={isSubmitting}
              >
                <option value={2}>Easy - For getting started, short practice rounds</option>
                <option value={3}>Medium - For some challenges, mid-level duration rounds</option>
                <option value={5}>Hard - For hands on, tricky & complex rounds</option>
              </select>
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <ChevronRight size={16} className="text-text-tertiary-light dark:text-text-tertiary-dark transform rotate-90" />
              </div>
            </div>
          </div>

          {/* Job Description */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <FileText size={18} className="text-primary-light dark:text-primary-dark" />
              <label className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark h-5">
                Job Description
              </label>
            </div>
            <div className={`rounded-lg border py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all ${
              validationErrors.jobDescription ? 'border-red-500 dark:border-red-400' : 'border-border-light dark:border-border-dark'
            }`}>
              <textarea
                placeholder="Paste the complete job description here, or describe the key responsibilities and requirements for this role..."
                rows={6}
                className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm resize-vertical"
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                disabled={isSubmitting}
              ></textarea>
            </div>
            {validationErrors.jobDescription && (
              <p className="text-red-500 dark:text-red-400 text-xs mt-2 flex items-center gap-1">
                <AlertCircle size={12} />
                {validationErrors.jobDescription}
              </p>
            )}
          </div>

          {/* Interview Language */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Globe size={18} className="text-primary-light dark:text-primary-dark" />
              <label className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark h-5">
                Interview Language
              </label>
            </div>
            <div className={`rounded-lg border py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all relative ${
              validationErrors.interviewLanguage ? 'border-red-500 dark:border-red-400' : 'border-border-light dark:border-border-dark'
            }`}>
              <select
                value={interviewLanguage}
                onChange={(e) => setInterviewLanguage(e.target.value)}
                className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm appearance-none pr-8"
                disabled={isSubmitting || isLoadingLanguages}
              >
                {languages.length > 0 ? (
                  languages.map(language => (
                    <option key={language._id} value={language.name}>
                      {language.name}
                    </option>
                  ))
                ) : (
                  // Fallback options if API fails
                  <>
                    <option value="English">English</option>
                  </>
                )}
              </select>
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <ChevronRight size={16} className="text-text-tertiary-light dark:text-text-tertiary-dark transform rotate-90" />
              </div>
            </div>
            {validationErrors.interviewLanguage && (
              <p className="text-red-500 dark:text-red-400 text-xs mt-2 flex items-center gap-1">
                <AlertCircle size={12} />
                {validationErrors.interviewLanguage}
              </p>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border-light dark:border-border-dark"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="px-4 bg-surface-light dark:bg-surface-dark text-sm text-text-tertiary-light dark:text-text-tertiary-dark font-medium">
              OR
            </span>
          </div>
        </div>

        {/* AI Generation Section */}
        <div className="mb-6 p-4 border-2 border-dashed border-primary-light/40 dark:border-primary-dark/40 rounded-lg bg-gradient-to-br from-primary-light/10 to-primary-light/5 dark:from-primary-dark/10 dark:to-primary-dark/5">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10">
              <Wand2 size={16} className="text-primary-light dark:text-primary-dark" />
            </div>
            <h4 className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark">
              Generate with AI
            </h4>
          </div>
          
          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-3">
            Describe your interview in <span className="text-text-primary-light dark:text-text-primary-dark font-medium">15-20 words</span> and let AI auto-fill the entire form for you.
          </p>
          
          <div className="flex gap-3 flex-col md:flex-row">
            <div className="flex-1">
              <div className="rounded-lg border border-border-light dark:border-border-dark py-1 bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all">
                <input
                  type="text"
                  placeholder="Min. 15-20 words, e.g. HR round for Oracle for sales executive"
                  className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm"
                  value={aiDescription}
                  onChange={(e) => setAiDescription(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !isGeneratingFormData && aiDescription.trim()) {
                      e.preventDefault();
                      handleGenerateFormData();
                    }
                  }}
                />
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              type="button"
              leftIcon={isGeneratingFormData ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
              disabled={aiDescription.length < 50 || isGeneratingFormData}
              onClick={handleGenerateFormData}
              className="py-3 mb-1 md:py-1 md:mb-0"
            >
              {isGeneratingFormData ? 'Generating...' : 'Generate'}
            </Button>
          </div>
          
          <div className="mt-3 mb-1 text-xs text-text-primary-light dark:text-text-primary-dark">
            💡 Try: "Machine code round for Google SWE", "HR interview at Microsoft", "Product manager case study at Amazon"
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex gap-3 justify-end">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <div className="relative group">
            <Button
              type="submit"
              variant="primary"
              leftIcon={isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <PlusIcon size={16} />}
              disabled={!isFormComplete || isSubmitting}
              className={!isFormComplete ? 'cursor-not-allowed' : ''}
            >
              {isSubmitting ? 'Creating...' : 'Create Interview'}
            </Button>
            {!isFormComplete && (
              <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-red-600 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <AlertCircle size={14} />
                  {getFirstValidationError() || 'Please fill in all required fields'}
                </div>
                <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-red-600"></div>
              </div>
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
};